import { useMemo, useState } from 'react';
import { Incident } from '../data/demoData';
import { ApiError, apiFetch } from '../lib/apiClient';
import { notifyIncidentExecutionUpdated } from '../lib/incidentExecutionEvents';

export interface IncidentStatusPayload {
  status: 'New' | 'Active';
  previousStatus?: string;
  statusComment?: string;
}

interface Props {
  incident: Incident;
  customerId?: string;
  initialStatus?: IncidentStatusPayload['status'];
  onCancel: () => void;
  onUpdated: (payload: IncidentStatusPayload) => void;
}

function effectiveStatus(i: Incident) {
  // Trust the status returned by Sentinel/Graph. Owner assignment is related
  // to triage, but it should not make the dialog reinterpret an Active
  // incident as New. The backend assigns the current analyst when moving an
  // XDR-backed incident to Active so the workspace view also stays Active.
  return (i.status || '').toLowerCase();
}

function titleForStatus(status: string) {
  const s = status.toLowerCase();
  if (s === 'closed') return 'Reopen Incident';
  return 'Change Incident Status';
}

function statusLabel(status: string) {
  const s = status.toLowerCase();
  if (s === 'new') return 'New';
  if (s === 'active') return 'Active / In Progress';
  if (s === 'closed') return 'Closed / Resolved';
  return status || 'Unknown';
}

export function IncidentStatusDialog({ incident, customerId, initialStatus, onCancel, onUpdated }: Props) {
  const currentStatus = effectiveStatus(incident);
  const defaultStatus = useMemo<'New' | 'Active'>(() => {
    if (currentStatus === 'active') return 'New';
    return 'Active';
  }, [currentStatus]);

  const [targetStatus, setTargetStatus] = useState<'New' | 'Active'>(initialStatus ?? defaultStatus);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fallbackPrompt, setFallbackPrompt] = useState<string | null>(null);
  const [fallbackAllowed, setFallbackAllowed] = useState(false);

  function isDelegatedFallbackPrompt(e: unknown): e is ApiError {
    return e instanceof ApiError && e.status === 409 && e.body?.['error'] === 'delegated_rbac_required';
  }

  async function submitStatus(useServicePrincipalFallback = false) {
    setLoading(true);
    setError(null);
    if (!useServicePrincipalFallback) { setFallbackPrompt(null); setFallbackAllowed(false); }

    const payload: IncidentStatusPayload = {
      status: targetStatus,
      previousStatus: incident.status,
      statusComment: comment.trim(),
    };

    try {
      const qs = customerId ? `?customerId=${encodeURIComponent(customerId)}` : '';
      const response = await apiFetch(`/incidents/${encodeURIComponent(incident.id)}/status${qs}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...payload,
          armIncidentName: incident.armIncidentName,
          graphIncidentId: incident.graphIncidentId,
          useServicePrincipalFallback,
        }),
      });
      void response;
      notifyIncidentExecutionUpdated(incident.id);
      onUpdated(payload);
    } catch (e) {
      if (isDelegatedFallbackPrompt(e)) {
        const msg = typeof e.body['message'] === 'string'
          ? e.body['message'] as string
          : 'Your user identity could not complete this delegated Sentinel action. Do you still wish to proceed using the Service Principal RBAC?';
        setFallbackPrompt(msg);
        setFallbackAllowed(e.body?.['canUseServicePrincipalFallback'] === true);
        setError(null);
      } else {
        setError(e instanceof Error ? e.message : String(e));
      }
      setLoading(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center px-4"
      onClick={onCancel}
    >
      <div
        className="bg-card rounded-xl shadow-2xl w-full max-w-md"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="mb-5">
            <h2 className="text-base font-bold text-foreground mb-1">{titleForStatus(currentStatus)}</h2>
            <p className="text-xs text-muted-foreground font-mono">
              #{incident.incidentNumber} · {incident.title.length > 60 ? incident.title.slice(0, 60) + '…' : incident.title}
            </p>
            <p className="text-[11px] text-muted-foreground mt-1">
              Current status: <span className="font-semibold">{statusLabel(currentStatus)}</span>
            </p>
          </div>

          <div className="mb-4">
            <label className="block text-xs font-semibold text-foreground mb-2">
              New status <span className="text-red-500">*</span>
            </label>
            <div className="space-y-2">
              {(['New', 'Active'] as const).map(status => (
                <label
                  key={status}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    targetStatus === status
                      ? 'border-primary bg-primary/5 dark:bg-primary/10'
                      : 'border-border hover:border-primary/50 hover:bg-muted/50'
                  }`}
                >
                  <input
                    type="radio"
                    name="targetStatus"
                    checked={targetStatus === status}
                    onChange={() => setTargetStatus(status)}
                    className="accent-primary"
                  />
                  <span className="text-sm text-foreground">
                    {status === 'Active' ? 'Active / In Progress' : 'New'}
                  </span>
                </label>
              ))}
            </div>
            {currentStatus === 'closed' && (
              <p className="text-[11px] text-amber-600 dark:text-amber-300 mt-2">
                Reopening a closed incident requires the <span className="font-semibold">incidents.reopen</span> capability and Sentinel write RBAC.
              </p>
            )}
            {targetStatus === 'Active' && (
              <p className="text-[11px] text-blue-600 dark:text-blue-300 mt-2">
                Active / In Progress will also assign the incident to your signed-in user so Microsoft Sentinel and Defender XDR keep it in an active triage state.
              </p>
            )}
          </div>

          <div className="mb-5">
            <label className="block text-xs font-semibold text-foreground mb-2">
              Analyst note <span className="text-muted-foreground font-normal">(optional)</span>
            </label>
            <textarea
              value={comment}
              onChange={e => setComment(e.target.value)}
              placeholder="Add a reason for this status change…"
              rows={3}
              className="w-full rounded-lg border border-border bg-background text-sm text-foreground px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-primary/50 placeholder:text-muted-foreground/60"
            />
          </div>

          {fallbackPrompt && (
            <div className="mb-4 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 p-3">
              <div className="text-xs font-semibold text-amber-900 dark:text-amber-200 mb-1">Delegated user action failed</div>
              <div className="text-xs text-amber-800 dark:text-amber-100 mb-3">{fallbackPrompt}</div>
              <div className="flex gap-2">
                {fallbackAllowed && (
                  <button
                    type="button"
                    onClick={() => submitStatus(true)}
                    disabled={loading}
                    className="px-3 py-1.5 rounded bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold disabled:opacity-50"
                  >
                    Yes, use Service Principal
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => { setFallbackPrompt(null); setFallbackAllowed(false); }}
                  disabled={loading}
                  className="px-3 py-1.5 rounded border border-amber-300 dark:border-amber-700 text-amber-900 dark:text-amber-100 text-xs font-semibold hover:bg-amber-100 dark:hover:bg-amber-900/40 disabled:opacity-50"
                >
                  No, cancel
                </button>
              </div>
            </div>
          )}

          {error && (
            <div className="mb-4 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 text-xs text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={onCancel}
              disabled={loading}
              className="flex-1 py-2.5 rounded-lg border border-border text-sm font-semibold text-foreground hover:bg-muted transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={() => submitStatus(false)}
              disabled={loading || targetStatus.toLowerCase() === currentStatus}
              className="flex-1 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin inline-block" />
                  Updating…
                </>
              ) : currentStatus === 'closed' ? 'Reopen Incident' : 'Update Status'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
